import React from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/context/AuthContext";
import { HeaderActions } from "@/components/common/HeaderActions";
import { cn } from "@/lib/utils";
import {
  Briefcase,
  FileText,
  GraduationCap,
  ClipboardCheck,
  Menu,
  Settings,
  X
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Loading } from "@/components/ui/loading";

const ROUTES_WITH_NO_PADDING = [
  /^\/talent\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^\/job-recognition-course/
];

export default function TalentLayout({
  children
}: {
  children?: React.ReactNode;
}) {
  const { t, ready } = useTranslation(["talent", "common"]);
  const { originalProfile } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isNoPaddingRoute = ROUTES_WITH_NO_PADDING.some((pattern) =>
    pattern instanceof RegExp
      ? pattern.test(location.pathname)
      : pattern === location.pathname
  );

  const navigation = [
    {
      name: t("nav_jobs"),
      href: "/jobs",
      icon: Briefcase
    },
    {
      name: t("nav_profile"),
      href: "/profile",
      icon: FileText
    },
    {
      name: t("nav_recognition"),
      href: "/job-recognition-course",
      icon: GraduationCap
    },
    {
      name: t("nav_exam_simulation"),
      href: "/exam-simulation",
      icon: ClipboardCheck
    },
    {
      name: t("nav_settings"),
      href: "/account",
      icon: Settings
    }
  ].filter((item) => {
    if (item.href === "/exam-simulation") {
      return originalProfile?.isSuperUser;
    }
    return true;
  });
  if (!ready) {
    return <Loading />;
  }

  return (
    <div className="h-screen flex">
      {/* Left side - Navigation and content */}
      <div className="w-full flex flex-col bg-gray-50">
        {/* Header with navigation */}
        <div className="bg-white border-b">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-6">
              {/* Mobile menu button */}
              <Button
                variant="outline"
                size="sm"
                className="md:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Menu className="h-4 w-4" />
                )}
              </Button>

              {/* Logo */}
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md bg-[#d769b3] flex items-center justify-center">
                  <span className="text-[#471999] font-semibold text-sm">
                    W
                  </span>
                </div>
                <span className="font-semibold hidden sm:block">Workinger</span>
              </div>

              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-1">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    location.pathname === item.href ||
                    (item.href === "/profile" &&
                      location.pathname.startsWith("/profile"));

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                        isActive
                          ? "bg-[#d769b3]/10 text-[#d769b3]"
                          : "text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <HeaderActions variant="menu" />
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-200">
            <nav className="px-4 py-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.href ||
                  (item.href === "/application" &&
                    location.pathname.startsWith("/application"));

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full",
                      isActive
                        ? "bg-[#d769b3]/10 text-[#d769b3]"
                        : "text-gray-600 hover:bg-gray-100"
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* Main content */}
        <main
          className={cn(
            "flex-1 overflow-y-auto",
            isNoPaddingRoute ? "p-0" : "p-4"
          )}
        >
          {/* 
            Render children if provided (when used as a wrapper component), 
            otherwise render Outlet for nested routes (when used as a layout route).
          */}
          {children || <Outlet />}
        </main>
      </div>

      {/* Right side - Ylvi chatbot (hidden on mobile) with animation */}
      {/*
      <div className="hidden lg:flex lg:w-1/2 bg-[#d769b3] border-l items-center justify-center overflow-hidden">
        <div
          className={cn(
            "w-full max-w-lg h-full transition-all duration-800 ease-out",
            isChatbotVisible
              ? "opacity-100 transform translate-x-0"
              : "opacity-0 transform translate-x-8"
          )}
        >
          <YlviChatbot />
        </div>
      </div>
      */}
    </div>
  );
}
